// src/utils/responsiveUtils.js
import { useTheme, useMediaQuery, Box, Grid, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { StyledTypography } from '../assets/css/styledComponent';
import { grey } from '@mui/material/colors';
import { UserContext } from '../components/auth/auth-context';

export const BookingStatus = {
  DELETED: 0,
  ACTIVE: 1, // active
  PENDING: 2,
};

export const StaffStatus = {
  DELETED: 0,
  ACTIVE: 1, // active
  PENDING: 2,
};

export const BusinessStatus = {
  DELETED: 0,
  ACTIVE: 1, // active
  PENDING: 2,
};

export const BookingTypeStatus = {
  DELETED: 0,
  ACTIVE: 1, // active
  PENDING: 2,
};

export const SubscriptionStatus = {
  CANCELLED: 0,
  ACTIVE: 1, // active
  PENDING_CHANGE: 2,
};
export const getEffectivePlan = (providerUser) => {
  // Convert the default planId to a number; if conversion fails, default to 0.
  const plan = Number(providerUser?.planId) || 0;

  // Determine whether the exempted plan is valid (i.e., non-expired).
  let isExemptedPlanValid = false;
  const fallbackTrialEndsAt = providerUser?.trialEndsAt
    ? new Date(providerUser.trialEndsAt)
    : null;

  if (providerUser?.exemptedPlanExpiresAt) {
    if (String(providerUser.exemptedPlanExpiresAt).toLowerCase() === "neverexpires") {
      // "noExpried" indicates the plan never expires.
      isExemptedPlanValid = true;
    } else {
      // Parse the date string to create a Date object.
      const expiryDate = new Date(providerUser.exemptedPlanExpiresAt);
      // Check if the parsed date is valid and lies in the future.
      if (!isNaN(expiryDate.getTime()) && expiryDate > new Date()) {
        isExemptedPlanValid = true;
      }
    }
  }
  if (
    !isExemptedPlanValid &&
    fallbackTrialEndsAt &&
    !Number.isNaN(fallbackTrialEndsAt.getTime()) &&
    fallbackTrialEndsAt > new Date()
  ) {
    isExemptedPlanValid = true;
  }

  // If the exempted plan is not valid (either expired or not provided), return the default plan.
  if (!isExemptedPlanValid) {
    return plan;
  }

  // Convert the exemptedPlanId to a number; if conversion fails, default to 0.
  const exemptedPlan = Number(providerUser.exemptedPlanId) || 0;
  // Backward-safe fallback: if trial is active but exempted plan id is missing, treat as premium.
  const fallbackTrialPlan = 3;
  const normalizedExemptedPlan = exemptedPlan > 1 ? exemptedPlan : fallbackTrialPlan;
  // Return the maximum of the two values, giving priority to the higher plan.
  return Math.max(plan, normalizedExemptedPlan);
};
export const getTrialDaysLeft = (providerUser) => {
  // Convert normal planId to a number; if conversion fails, default to 0.
  const normalPlan = Number(providerUser?.planId) || 0;

  // Only compute trial days if the normal plan is less than 2.
  if (normalPlan >= 2) {
    return 0;
  }

  // If there is no expiration date for the trial, return 0.
  if (!providerUser?.exemptedPlanExpiresAt && !providerUser?.trialEndsAt) {
    return 0;
  }

  // Check if the trial expiration indicates a non-expiring trial.
  if (String(providerUser?.exemptedPlanExpiresAt || "").toLowerCase() === "neverexpires") {
    // Return a special value to indicate unlimited trial days.
    return "Unlimited";
  }

  // Parse the trial expiration date from the provided string.
  const trialExpiry = providerUser?.exemptedPlanExpiresAt
    ? new Date(providerUser.exemptedPlanExpiresAt)
    : new Date(providerUser?.trialEndsAt);
  const now = new Date();

  // If the trial expiry has passed, return 0.
  if (trialExpiry <= now) {
    return 0;
  }

  // Calculate the difference between the expiry date and now in milliseconds.
  const diffTime = trialExpiry - now;

  // Convert milliseconds to days and round up to the nearest whole number.
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return daysLeft;
};

export const TrialNotification = () => {
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;

  const trialDaysLeft = getTrialDaysLeft(providerUser);

  return (
    <Box mx="auto" textAlign="center" style={{ color: '#1976d2', maxWidth: '600px' }}>
      <Typography variant="h6">
        {trialDaysLeft === "Unlimited"
          ? ""
          : trialDaysLeft > 0
            ? `Business trial ends in ${trialDaysLeft} day${trialDaysLeft > 1 ? "s" : ""}`
            : ""
        }
      </Typography>
    </Box>
  );
};


export function getCategoryName(categoryId) {
  return categoryId === 1 ? "Events" : categoryId === 2 ? "Services" : "Classes";
}

export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
};

export const TruncatedTextComponent = ({ text, maxWords = 20 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const sanitizedText = DOMPurify.sanitize(text || "");
  const words = sanitizedText.split(" ");
  const isTruncated = words.length > maxWords;
  const truncatedText = isTruncated
    ? `${words.slice(0, maxWords).join(" ")}...`
    : sanitizedText;

  return (
    <Box>
      <Typography
        component="span"
        sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
        dangerouslySetInnerHTML={{
          __html: isExpanded ? sanitizedText : truncatedText,
        }}
      />
      {isTruncated && (
        <Typography
          component="span"
          onClick={toggleExpanded}
          sx={{
            color: "#22374a",
            cursor: "pointer",
            fontSize: "0.8rem",
            fontWeight: 400,
            ml: 0.5,
          }}
        >
          {isExpanded ? "Show Less" : "Show More"}
        </Typography>
      )}
    </Box>
  );
};
export function useCurrentUser() {
  const { providerUser } = useContext(UserContext);

  return {
    userId: providerUser?.id || null,
    staffId: providerUser?.staffId || null,
    providerUser,
  };
}

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
export const DescriptionComponent = ({ selectedBookingData }) => {
  return (
    <>
      {/* Description Container */}
      <Box
        sx={{
          borderRadius: "4px", // Rounded corners 
          backgroundColor: grey[50],
          "&:hover": {
            borderColor: "#ccc", // Darker border on hover
          },
          width: '100%',
          pl: 2,
          pr: 2,
        }}
      >
        <Grid container >
          <Grid
            item
            xs
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "text.primary", // Normal dark text
                whiteSpace: "normal",
              }}
            >
              <TruncatedTextComponent text={selectedBookingData?.desc || ""} maxWords={24} />
            </Typography>

          </Grid>
        </Grid>
      </Box>
    </>
  );
};


export const InfoRow = ({ icon, text }) => (
  <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
    {icon}
    <StyledTypography>{text}</StyledTypography>
  </Box>
);

export const formatDuration = (length) => {
  // Validate input: length must be a non-negative number
  if (typeof length !== "number" || length < 0) {
    return "Invalid duration";
  }

  // If the duration is less than 60 minutes, return it in minutes
  if (length < 60) {
    return `${length} ${length === 1 ? "min" : "mins"}`;
  }

  // Convert minutes to hours
  const hours = length / 60;

  // Format hours: if the value is an integer, show without decimal, otherwise use one decimal place
  const formattedHours = Number.isInteger(hours) ? hours : hours.toFixed(1);

  // Return the formatted string with proper singular/plural for 'hr'
  return `${formattedHours} ${hours === 1 ? "hr" : "hrs"}`;
};

export function hasContactInfo(text = "") {
  // Regex for emails
  const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  // Regex for phone numbers (basic, covers common formats)
  const phoneRegex = /(\+?\d{1,3}[-.\s]?)?(\(?\d{2,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{3,4}/;

  return emailRegex.test(text) || phoneRegex.test(text);
} 
